/**
 * MapLibre's worker, which the bundler had no reason to ship and therefore did not.
 *
 * MapLibre GL 6 stopped inlining its worker as a blob. It now derives the URL from the bundle's
 * own location:
 *
 * ```js
 * new URL('./maplibre-gl-worker.mjs', import.meta.url)
 * ```
 *
 * That file sits in `maplibre-gl/dist`, nothing in this project imported it, so Vite never
 * emitted it — and the built app asked the server for `/assets/maplibre-gl-worker.mjs`.
 *
 * Here that was worse than a 404. `serveStatic` falls back to the single-page-app entry
 * document for any path that is not a file, so the request came back **200 with
 * `text/html`**: a healthy-looking response containing an HTML page where a module was
 * expected. `new Worker(url, { type: 'module' })` rejects it on MIME type, and **nothing
 * throws** — MapLibre does not listen for `error` on the worker, so the actor simply never
 * answers and its pending callbacks pile up forever.
 *
 * The symptom set, which is why this looked like several unrelated bugs:
 *
 *   * the basemap never drew a pixel and never requested a single tile;
 *   * the density surface and the hexagons were missing — **GeoJSON is parsed in the worker
 *     too**, so our own layers died with the basemap;
 *   * `map.on('error')` never fired, so the "Karte konnte nicht geladen werden" notice that
 *     exists for exactly this stayed hidden;
 *   * and the console was completely empty.
 *
 * flightlogd hit the identical bug; `web/src/lib/maplibre.ts` there tells the same story.
 * Two projects, one root cause, so the fix is deliberately the same shape in both.
 *
 * `?worker&url` and not the obvious `?url`: the shipped worker is only a small shim that does
 * `import './maplibre-gl-shared.mjs'` for the bulk of the code. `?url` copies that one file
 * verbatim, so the missing-asset problem simply moves down a level and the worker still never
 * boots. `?worker` runs it through Vite's own worker build, which follows the import and emits
 * one self-contained, fingerprinted module — fingerprinted being the part that matters,
 * because the server sends `/assets/*` as `immutable` and may only do that if every name
 * carries a hash.
 */

import { setWorkerUrl } from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';

setWorkerUrl(workerUrl);

/**
 * Whether the worker asset is really a module, for the map to say so rather than hang.
 *
 * The content type is the whole check. A 404 would have been loud; a 200 carrying
 * `text/html` is what this bug actually produced, and it is indistinguishable from success
 * unless somebody looks at the header. Resolves `false` rather than rejecting, because the
 * caller wants a message on the screen, not an exception.
 */
export async function workerIsUsable(): Promise<boolean> {
  try {
    const response = await fetch(workerUrl, { method: 'GET' });
    if (!response.ok) return false;
    return (response.headers.get('content-type') ?? '').includes('javascript');
  } catch {
    return false;
  }
}

export { workerUrl };

/**
 * Collapse the attribution control, which MapLibre deliberately opens on first render.
 *
 * `compact: true` only promises a *collapsible* control, not a collapsed one: on the first
 * update `_updateCompact` adds `maplibregl-compact` **and** `maplibregl-compact-show`
 * together, so the full licence text — two providers, three URLs, about 120 characters — lies
 * across the bottom of the map until somebody taps the ⓘ. On a phone that is a banner over
 * the report button, not an attribution.
 *
 * The one thing that makes this stable rather than a fight with the library: MapLibre only
 * adds the pair when `maplibregl-compact` is **not already present**. Setting that class
 * ourselves, once, takes the branch out of play for good — every later data event leaves the
 * control alone, and the ⓘ toggle keeps working exactly as it should.
 *
 * The attribution is not hidden and must not be: OpenStreetMap's licence requires it. It is
 * one tap away and still in the DOM, which is what the licence asks for.
 */
export function collapseAttribution(map: {
  getContainer(): HTMLElement;
  once(type: 'idle', listener: () => void): unknown;
}): void {
  const collapse = () => {
    const el = map.getContainer().querySelector('.maplibregl-ctrl-attrib');
    if (el === null) return;
    el.classList.add('maplibregl-compact');
    el.classList.remove('maplibregl-compact-show');
    el.removeAttribute('open');
  };
  collapse();
  // Again once the map settles: on a cold load the control can be created empty, and an empty
  // one carries `maplibregl-attrib-empty`, which makes MapLibre skip the branch above and take
  // it later instead — after the first tiles arrive with their attribution.
  map.once('idle', collapse);
}
